
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, View, FlatList, TouchableOpacity, useWindowDimensions } from 'react-native';
import { ZenBadge, ZenText, ZenLoader, ZenDateTimePicker, ZenIcon, ZenToast, ZenEmptyComponent } from 'zen-core-elements';
import { fontSize } from '../../../themes/appTheme';
import { appColors } from 'zen-core-elements/src/shared/constants';
import CommonHeader from '../../../shared/commonComponents/commonHeader';
import { useAppNavigation, useAppRoute } from '../../../types/@react-navigation/navigation';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import CommonService from '../../../shared/services/commonService';
import { useIsFocused } from '@react-navigation/native';
import moment from 'moment';
import { getAllTaxReport } from '../../../redux/slices/taxReport.slice';
import { config } from '../../../shared/environment';
import { TaxSummaryItem } from './taxReport.module';
import useBackHandler from '../../../shared/hooks/useBackHandler';
import { showApiError } from '../../../shared/utils/handleApiError';


let offset = 0;

const TaxReport = () => {
    useBackHandler()
    const isFocus = useIsFocused();
    const navigation = useAppNavigation();
    const dispatch = useAppDispatch();
    const { width, height } = useWindowDimensions();
    const isLandscape = useMemo(() => width > height, [width, height])
    const { id: locationId } = useAppSelector(state => state?.common?.currentLocation)

    const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
    const route = useAppRoute("TaxReport")
    //Variable to handle open state of the date picker component
    const [datePickerVisibile, setDatePickerVisible] = useState<{
        visibilty: boolean,
        current: 'from' | 'to' | null
    }>({
        visibilty: false, current: null
    })

    const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
        from: route?.params?.beginDate!,
        to: route?.params?.endDate!,
    });
    //variable for handle refresh control
    const [listRefresh, setListRefresh] = useState(false)
    const { loading, value, isReached, filter } = useAppSelector((state) => state?.taxReport?.taxReportData);

    useEffect(() => {
        if (isFocus) {
            offset = 0;
            getAllTaxReports(listRefresh ? 0 : offset, listRefresh);
        }
    }, [isFocus, dateRange]);

    const getAllTaxReports = async (currentOffset?: number, forceRefresh = false) => {
        const payload = {
            limit: 10,
            offset: currentOffset !== undefined ? currentOffset : 0,
            filterData: JSON.stringify({ customDateRange: { begin: dateRange?.from, end: dateRange?.to } }),
            storeId: config.storeId,
            locationId
        }
        if (!forceRefresh && payload.offset === 0 && filter === payload.filterData && value) return
        dispatch(getAllTaxReport({
            data: payload
        })).unwrap().catch(showApiError).finally(() => {
            setListRefresh(false)
        })
    };



    const StatCard = ({ title, value, iconName, color }: { title: string, value: number, iconName: string, color: string }) => (
        <View style={[styles.card, !isLandscape && styles.cardPortrait]}>
            <View style={[styles.iconContainer, !isLandscape && styles.iconContainerPortrait, { backgroundColor: color + '15' }]}>
                <ZenIcon type='material-community' name={iconName} size={isLandscape ? 28 : 22} color={color} />
            </View>
            <View style={{ flex: 1 }}>
                <ZenText content={title} size={fontSize.smallHeaderFontSize} color={appColors.lightDark} style={{ marginBottom: 2 }} />
                <ZenText content={CommonService.currencyStandard(currencySymbol, String(value ?? 0))} bold color={appColors.dark} size={fontSize.largerFontSize} />
            </View>
        </View>
    );

    const renderTableHeader = () => (
        <View style={styles.tableHeader}>
            <ZenText content="TAX NAME" size={fontSize.errorFontSize} color={appColors.lightDark} style={[styles.headerText, { flex: 2 }]} />
            <ZenText content="TAX RATE" size={fontSize.errorFontSize} color={appColors.lightDark} style={[styles.headerText, { flex: 1, textAlign: 'center' }]} />
            <ZenText content="TAXABLE SALES" size={fontSize.errorFontSize} color={appColors.lightDark} style={[styles.headerText, { flex: 1, textAlign: 'right' }]} />
            <ZenText content="TAX AMOUNT" size={fontSize.errorFontSize} color={appColors.lightDark} style={[styles.headerText, { flex: 1, textAlign: 'right' }]} />
        </View>
    );

    const renderItem = ({ item }: { item: TaxSummaryItem }) => {
        if (!isLandscape) {
            return (
                <View style={styles.mobileCard}>
                    <View style={styles.mobileCardHeader}>
                        <ZenText content={item.taxname} bold size={fontSize.smallHeaderFontSize} color={appColors.dark} style={{ flex: 1, marginRight: 10 }} />
                        <ZenBadge
                            badgeName={`${item.taxrate}%`}
                            badgeTextSize={fontSize.smallHeaderFontSize}
                            badgeTextColor={appColors.primary}
                            badgeColor={appColors.lightPrimary}
                        />
                    </View>
                    <View style={styles.mobileCardBody}>
                        <View style={styles.mobileCardRow}>
                            <View style={styles.mobileCardCol}>
                                <ZenText content="Taxable Sales" size={fontSize.errorFontSize} color={appColors.lightDark} style={{ marginBottom: 2 }} />
                                <ZenText content={CommonService.currencyStandard(currencySymbol, String(item.taxablesales ?? 0))} bold size={fontSize.smallHeaderFontSize} color={appColors.dark} />
                            </View>
                            <View style={styles.mobileCardCol}>
                                <ZenText content="Tax Amount" size={fontSize.errorFontSize} color={appColors.lightDark} style={{ marginBottom: 2 }} />
                                <ZenText content={CommonService.currencyStandard(currencySymbol, String(item.taxamount ?? 0))} bold size={fontSize.smallHeaderFontSize} color={appColors.primary} />
                            </View>
                        </View>
                    </View>
                </View>
            );
        }

        return (
            <View style={styles.tableRow}>
                <ZenText content={item.taxname} bold size={fontSize.smallHeaderFontSize} color={appColors.dark} style={{ flex: 2 }} />
                <ZenText content={`${item.taxrate}%`} size={fontSize.smallHeaderFontSize} color={appColors.dark} style={{ flex: 1, textAlign: 'center' }} />
                <ZenText content={CommonService.currencyStandard(currencySymbol, String(item.taxablesales ?? 0))} size={fontSize.smallHeaderFontSize} color={appColors.dark} style={{ flex: 1, textAlign: 'right' }} />
                <ZenText content={CommonService.currencyStandard(currencySymbol, String(item.taxamount ?? 0))} bold size={fontSize.smallHeaderFontSize} color={appColors.dark} style={{ flex: 1, textAlign: 'right' }} />
            </View>
        );
    };

    const ListHeader = () => {
        const statsData = [
            {
                id: '1',
                title: "Taxable Sales",
                value: value?.taxReport?.overallSummary?.taxable_sales || 0,
                iconName: "file-percent",
                color: "#3B82F6"
            },
            {
                id: '2',
                title: "Non-taxable Sales",
                value: value?.taxReport?.overallSummary?.non_taxable_sales || 0,
                iconName: "file-check",
                color: "#10B981"
            },
            {
                id: '3',
                title: "Total Net Sales",
                value: value?.taxReport?.overallSummary?.total_net_sales || 0,
                iconName: "chart-line",
                color: "#8B5CF6"
            },
            {
                id: '4',
                title: "Total Tax Amount",
                value: value?.taxReport?.overallSummary?.total_tax_amount || 0,
                iconName: "bank",
                color: "#EF4444"
            }
        ];

        return (
            <View>
                {/* KPI Grid - Responsive */}
                <FlatList
                    data={statsData}
                    renderItem={({ item }) => (
                        <StatCard
                            title={item.title}
                            value={item.value}
                            iconName={item.iconName}
                            color={item.color}
                        />
                    )}
                    keyExtractor={(item) => item.id}
                    numColumns={isLandscape ? 4 : 1}
                    scrollEnabled={false}
                    style={{ marginHorizontal: -8, marginBottom: 20 }}
                />

                {/* Table Header Section */}
                {value?.taxReport?.taxSummary?.length! > 0 && <>
                    <View style={styles.tableTitleRow}>
                        <ZenText content="Tax Breakdowns" size={fontSize.largerFontSize} color={appColors.lightDark} bold />
                    </View>
                    {isLandscape && renderTableHeader()}
                </>}
            </View>
        );
    };

    const DateFilterBar = () => (
        <View style={styles.unifiedBar}>
            <TouchableOpacity style={styles.dateSegment} onPress={() => setDatePickerVisible({ visibilty: true, current: 'from' })}>
                <ZenIcon type='material-community' name="calendar-range" size={20} color={appColors.primary} />
                <ZenText content={moment(dateRange?.from).format('DD MMM, YYYY')} size={fontSize.tinyFontSize} color={appColors.primary} />
            </TouchableOpacity>

            <View style={styles.verticalDivider} />

            <TouchableOpacity style={styles.dateSegment} onPress={() => setDatePickerVisible({ visibilty: true, current: 'to' })}>
                <ZenText content={moment(dateRange?.to).format('DD MMM, YYYY')} size={fontSize.tinyFontSize} color={appColors.primary} />
                <ZenIcon type='material-community' name="chevron-down" size={18} color={appColors.primary} />
            </TouchableOpacity>
        </View>
    );


    const reInitializeStates = () => {
        setDateRange({ from: route?.params?.beginDate!, to: route?.params?.endDate! });
    }
    return (
        <View style={styles.container}>
            <CommonHeader
                headerName='Tax Report'
                backIcon={true}
                backIconOnpress={() => navigation.goBack()}
            />
            {/* Date Filter */}
            <DateFilterBar />

            <View style={styles.contentContainer}>
                {(loading && !offset && (!listRefresh || !value?.taxReport)) ? (
                    <ZenLoader lottieType='Local' lottie={require("../../../assets/infinityLoader.json")} />
                ) : (
                    <FlatList
                        data={value?.taxReport?.taxSummary ? value?.taxReport.taxSummary : []}
                        keyExtractor={(item, index) => index.toString()}
                        progressViewOffset={isLandscape ? 140 : 280}
                        renderItem={renderItem}
                        ListHeaderComponent={ListHeader}
                        ListEmptyComponent={ZenEmptyComponent}
                        onEndReached={() => {
                            if (!isReached && !loading) {
                                offset = offset + 10;
                                getAllTaxReports(offset);
                            }
                        }}
                        onRefresh={() => {
                            setListRefresh(true)
                            reInitializeStates()
                        }}
                        refreshing={listRefresh}
                        onEndReachedThreshold={0.5}
                        ListFooterComponentStyle={{ height: value?.taxReport?.taxSummary?.length! > 0 ? 20 : undefined }}
                        ListFooterComponent={!isReached && value?.taxReport?.taxSummary?.length! > 0 ? <ZenLoader size={'small'} color={appColors.primary} /> : null}
                        showsVerticalScrollIndicator={false}
                        contentContainerStyle={{
                            paddingBottom: value?.taxReport?.taxSummary?.length! > 0 ? 10 : undefined,
                            flex: value?.taxReport?.taxSummary?.length! > 0 ? undefined : 1,
                            gap: value?.taxReport?.taxSummary?.length! > 0 ? 15 : undefined,

                        }}
                    />
                )}
            </View>
            {
                datePickerVisibile?.visibilty && <ZenDateTimePicker
                    date={datePickerVisibile?.current === 'from' ? new Date(dateRange?.from) : new Date(dateRange?.to)}
                    mode="date"
                    open={datePickerVisibile?.visibilty}
                    maximumDate={
                        datePickerVisibile?.current == 'from' && dateRange?.to
                            ? new Date(dateRange?.to)
                            : new Date()
                    }
                    minimumDate={
                        datePickerVisibile?.current !== 'from' && dateRange?.from
                            ? new Date(dateRange?.from)
                            : undefined
                    }
                    onConfirm={(data) => {
                        setDatePickerVisible({
                            current: null,
                            visibilty: false
                        });

                        if (datePickerVisibile?.current == 'from') {
                            setDateRange(prev => {
                                return {
                                    ...prev,
                                    from: moment(data).format('YYYY-MM-DD')
                                }
                            })
                        }
                        else {
                            setDateRange(prev => {
                                return {
                                    ...prev,
                                    to: moment(data).format('YYYY-MM-DD')
                                }
                            })
                        }
                    }}
                    onCancel={() => {
                        setDatePickerVisible({ visibilty: false, current: null });
                    }}
                />
            }
        </View>
    );
};

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: appColors.lightBackgroundColor },
    contentContainer: { flex: 1, padding: 15 },

    // Date Filter Styles - New
    unifiedBar: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFF',
        borderRadius: 16,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        alignSelf: 'flex-end',
        marginTop: 15,
        marginHorizontal: 15
    },
    dateSegment: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, gap: 8 },
    verticalDivider: { width: 1.5, height: 22, backgroundColor: '#E2E8F0', marginHorizontal: 4 },

    // KPI Card Grid
    statsGrid: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -8, marginBottom: 20 },
    card: { flex: 1, minWidth: 220, margin: 8, backgroundColor: '#FFF', padding: 24, borderRadius: 20, flexDirection: 'row', alignItems: 'center', shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 10, elevation: 2 },
    cardPortrait: {
        minWidth: 120,
        padding: 12,
        margin: 6,
        borderRadius: 16,
    },
    iconContainer: { padding: 14, borderRadius: 16, marginRight: 18 },
    iconContainerPortrait: {
        padding: 10,
        marginRight: 10,
    },

    // Table Styles
    tableTitleRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, paddingHorizontal: 14 },
    tableHeader: { flexDirection: 'row', padding: 16, borderBottomWidth: 1, borderColor: '#F1F5F9', backgroundColor: '#FFF', borderTopLeftRadius: 24, borderTopRightRadius: 24 },
    headerText: { letterSpacing: 0.5 },
    tableRow: { flexDirection: 'row', paddingVertical: 20, paddingHorizontal: 16, borderBottomWidth: 1, borderColor: '#F8FAFC', alignItems: 'center', backgroundColor: '#FFF' },

    // Mobile Card Styles
    mobileCard: {
        backgroundColor: '#FFF',
        borderRadius: 16,
        padding: 16,
        borderWidth: 1,
        borderColor: '#F1F5F9',
        marginBottom: 10,
        elevation: 1,
    },
    mobileCardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
        paddingBottom: 10,
        marginBottom: 10,
    },
    mobileCardBody: {
        flexDirection: 'column',
    },
    mobileCardRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    mobileCardCol: {
        flex: 1,
        alignItems: 'flex-start',
    },

    emptyContainer: { flex: 1, marginTop: '20%', alignItems: 'center' },
    filterButton: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#FFF', padding: 5, borderRadius: 7 }
});

export default TaxReport;
